import React, { useState, useCallback } from 'react';
import { getErrorMessage } from '../utils/errorHandler';

interface JournalImportProps {
  onMenuSelect: (menuId: string) => void;
  tenantName: string;
  currentUser: any;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentTenantId: number | null;
}

interface ImportError {
  row: number;
  column?: string;
  value?: string;
  message: string;
}

interface ImportValidationResult {
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  errors: ImportError[];
  preview_data: Array<{
    kihyoubi: string;
    karikata_kamoku_id: number;
    karikata_kingaku: number;
    kashikata_kamoku_id: number;
    kashikata_kingaku: number;
    tekiyou?: string;
    comment?: string;
  }>;
  debit_total: number;
  credit_total: number;
}

interface ImportResult {
  import_id: string;
  status: string;
  imported_rows: number;
  skipped_rows: number;
  total_amount: {
    debit: number;
    credit: number;
  };
}

const JournalImport: React.FC<JournalImportProps> = ({
  onMenuSelect,
  tenantName,
  currentUser,
  apiFetch,
  currentTenantId,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validationResult, setValidationResult] = useState<ImportValidationResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (droppedFile.name.endsWith('.csv') || droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls')) {
        setFile(droppedFile);
        setError(null);
        setValidationResult(null);
        setImportResult(null);
      } else {
        setError('CSVまたはExcelファイル（.xlsx, .xls）を選択してください');
      }
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setError(null);
        setValidationResult(null);
        setImportResult(null);
      } else {
        setError('CSVまたはExcelファイル（.xlsx, .xls）を選択してください');
      }
    }
  }, []);

  const handlePreview = useCallback(async () => {
    if (!file) {
      setError('ファイルを選択してください');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('preview', 'true');

      const token = localStorage.getItem('auth_token');
      const response = await apiFetch('/api/journal-entries/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorMessage = await getErrorMessage(response, 'プレビューに失敗しました');
        throw new Error(errorMessage);
      }

      const data: ImportValidationResult = await response.json();
      setValidationResult(data);
    } catch (err: any) {
      setError(err?.message || 'プレビューに失敗しました');
    } finally {
      setUploading(false);
    }
  }, [file, apiFetch]);

  const handleImport = useCallback(async () => {
    if (!file) {
      setError('ファイルを選択してください');
      return;
    }

    if (!validationResult || validationResult.error_rows > 0) {
      setError('エラーがあるためインポートできません。エラーを修正してから再度アップロードしてください。');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('preview', 'false');

      const token = localStorage.getItem('auth_token');
      const response = await apiFetch('/api/journal-entries/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorMessage = await getErrorMessage(response, 'インポートに失敗しました');
        throw new Error(errorMessage);
      }

      const data: ImportResult = await response.json();
      setImportResult(data);
      setValidationResult(null);
      setFile(null);
    } catch (err: any) {
      setError(err?.message || 'インポートに失敗しました');
    } finally {
      setUploading(false);
    }
  }, [file, validationResult, apiFetch]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
  };

  return (
    <div style={{ padding: '20px', fontFamily: "'MS UI Gothic', 'Hiragino Kaku Gothic ProN', sans-serif" }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>
          CSV/Excel仕訳インポート
        </h2>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '20px' }}>
          既存システムからエクスポートされたCSV/Excelファイルの仕訳データをインポートします。
        </p>
      </div>

      {/* ステップ1: ファイル選択 */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>
          ステップ1: ファイル選択
        </h3>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: `2px dashed ${isDragging ? '#0066cc' : '#ccc'}`,
            borderRadius: '8px',
            padding: '40px',
            textAlign: 'center',
            backgroundColor: isDragging ? '#f0f7ff' : '#fff',
            cursor: 'pointer',
            transition: 'all 0.3s',
          }}
        >
          {file ? (
            <div>
              <p style={{ fontSize: '14px', color: '#0066cc', marginBottom: '10px' }}>
                ✓ {file.name}
              </p>
              <button
                onClick={() => {
                  setFile(null);
                  setValidationResult(null);
                  setImportResult(null);
                }}
                style={{
                  padding: '5px 15px',
                  fontSize: '12px',
                  backgroundColor: '#ff4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                ファイルを削除
              </button>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                ファイルをドラッグ&ドロップ
              </p>
              <p style={{ fontSize: '12px', color: '#999', marginBottom: '15px' }}>
                または
              </p>
              <label
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  backgroundColor: '#0066cc',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                ファイル選択
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </label>
              <p style={{ fontSize: '11px', color: '#999', marginTop: '10px' }}>
                対応形式: CSV, Excel (.xlsx, .xls)
              </p>
            </div>
          )}
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div
          style={{
            padding: '10px',
            backgroundColor: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '4px',
            marginBottom: '20px',
            color: '#c62828',
            fontSize: '12px',
          }}
        >
          {error}
        </div>
      )}

      {/* 統計情報 */}
      {validationResult && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>
            統計情報
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
            <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <div style={{ fontSize: '11px', color: '#666' }}>総件数</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{validationResult.total_rows}件</div>
            </div>
            <div style={{ padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
              <div style={{ fontSize: '11px', color: '#666' }}>正常件数</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2e7d32' }}>
                {validationResult.valid_rows}件
              </div>
            </div>
            <div style={{ padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
              <div style={{ fontSize: '11px', color: '#666' }}>エラー件数</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#c62828' }}>
                {validationResult.error_rows}件
              </div>
            </div>
            <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <div style={{ fontSize: '11px', color: '#666' }}>借方合計</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                {formatCurrency(validationResult.debit_total)}
              </div>
            </div>
            <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <div style={{ fontSize: '11px', color: '#666' }}>貸方合計</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                {formatCurrency(validationResult.credit_total)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* エラー一覧 */}
      {validationResult && validationResult.errors.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px', color: '#c62828' }}>
            エラー一覧
          </h3>
          <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>行番号</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>エラー内容</th>
                </tr>
              </thead>
              <tbody>
                {validationResult.errors.map((err, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{err.row}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #eee', color: '#c62828' }}>
                      {err.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* プレビューデータ */}
      {validationResult && validationResult.preview_data.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>
            プレビュー（最大100件）
          </h3>
          <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                <tr>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>記帳日</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>借方科目ID</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>借方金額</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>貸方科目ID</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>貸方金額</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>摘要</th>
                </tr>
              </thead>
              <tbody>
                {validationResult.preview_data.map((row, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row.kihyoubi}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row.karikata_kamoku_id}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                      {formatCurrency(row.karikata_kingaku)}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row.kashikata_kamoku_id}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                      {formatCurrency(row.kashikata_kingaku)}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{row.tekiyou || row.comment || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* インポート成功メッセージ */}
      {importResult && (
        <div
          style={{
            padding: '15px',
            backgroundColor: '#e8f5e9',
            border: '1px solid #4caf50',
            borderRadius: '4px',
            marginBottom: '20px',
            color: '#2e7d32',
            fontSize: '12px',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>✓ インポートが完了しました</p>
          <p>インポート件数: {importResult.imported_rows}件</p>
          <p>スキップ件数: {importResult.skipped_rows}件</p>
        </div>
      )}

      {/* ボタン */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        <button
          onClick={handlePreview}
          disabled={!file || uploading}
          style={{
            padding: '10px 20px',
            backgroundColor: file && !uploading ? '#0066cc' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: file && !uploading ? 'pointer' : 'not-allowed',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          {uploading ? '処理中...' : 'プレビュー'}
        </button>
        <button
          onClick={handleImport}
          disabled={!validationResult || validationResult.error_rows > 0 || uploading}
          style={{
            padding: '10px 20px',
            backgroundColor: validationResult && validationResult.error_rows === 0 && !uploading ? '#4caf50' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: validationResult && validationResult.error_rows === 0 && !uploading ? 'pointer' : 'not-allowed',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          {uploading ? 'インポート中...' : 'インポート実行'}
        </button>
        <button
          onClick={() => onMenuSelect('main-menu')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#999',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          キャンセル
        </button>
      </div>
    </div>
  );
};

export default JournalImport;
